/**
 * Formatting helpers shared by every Spotlight component.
 *
 * The project has no date-fns and no Intl wrapper, so these are hand-rolled in
 * the same style as src/utils/FeedTransformData.js. Every one of them tolerates
 * null, undefined and unparseable input, because the data behind this page is
 * scraped and any field can be missing on any row.
 */

const toDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "April 23, 2026" */
export const formatDate = (value) => {
  const date = toDate(value)
  if (!date) return null
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

/** "23 Apr" — the compact form used where space is tight. */
export const formatShortDate = (value) => {
  const date = toDate(value)
  if (!date) return null
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * "16–22 Sep 2026" for a range, "16 Sep 2026" for a single day.
 * Falls back to whichever end of the range exists.
 */
export const formatDateRange = (start, end) => {
  const from = toDate(start)
  const to = toDate(end)

  if (!from && !to) return null
  if (!from) return formatDate(to)
  if (!to) return formatDate(from)

  const sameDay = from.toDateString() === to.toDateString()
  if (sameDay) return formatDate(from)

  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()
  if (sameMonth) {
    return `${from.getDate()}–${to.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })}`
  }

  const sameYear = from.getFullYear() === to.getFullYear()
  const fromPart = from.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
  const toPart = to.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  return `${fromPart} – ${toPart}`
}

/** "3 days ago", "in 2 weeks", "today". */
export const timeAgo = (value) => {
  const date = toDate(value)
  if (!date) return null

  const diffMs = Date.now() - date.getTime()
  const future = diffMs < 0
  const seconds = Math.abs(diffMs) / 1000

  const units = [
    { limit: 60, label: 'just now', divisor: 1, exact: true },
    { limit: 3600, label: 'minute', divisor: 60 },
    { limit: 86400, label: 'hour', divisor: 3600 },
    { limit: 604800, label: 'day', divisor: 86400 },
    { limit: 2592000, label: 'week', divisor: 604800 },
    { limit: 31536000, label: 'month', divisor: 2592000 },
    { limit: Infinity, label: 'year', divisor: 31536000 },
  ]

  for (const unit of units) {
    if (seconds >= unit.limit) continue
    if (unit.exact) return 'just now'
    const amount = Math.max(1, Math.floor(seconds / unit.divisor))
    const plural = amount === 1 ? unit.label : `${unit.label}s`
    return future ? `in ${amount} ${plural}` : `${amount} ${plural} ago`
  }

  return null
}

/** 1 -> "1", 1240 -> "1.2K", 2800000 -> "2.8M". Mirrors the server's helper. */
export const compactNumber = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0'
  if (n < 1000) return String(n)
  if (n < 1000000) {
    const k = n / 1000
    return `${k < 10 ? k.toFixed(1).replace(/\.0$/, '') : Math.round(k)}K`
  }
  const m = n / 1000000
  return `${m < 10 ? m.toFixed(1).replace(/\.0$/, '') : Math.round(m)}M`
}

/**
 * "450K–600K SEK", "from 450K SEK", "up to 600K SEK".
 * Returns null when neither bound is present, which is the common case.
 */
export const formatSalary = (min, max, currency) => {
  const lo = Number.isFinite(Number(min)) && Number(min) > 0 ? Number(min) : null
  const hi = Number.isFinite(Number(max)) && Number(max) > 0 ? Number(max) : null
  if (lo === null && hi === null) return null

  const suffix = currency ? ` ${String(currency).toUpperCase()}` : ''

  if (lo !== null && hi !== null) {
    if (lo === hi) return `${compactNumber(lo)}${suffix}`
    return `${compactNumber(lo)}–${compactNumber(hi)}${suffix}`
  }
  if (lo !== null) return `from ${compactNumber(lo)}${suffix}`
  return `up to ${compactNumber(hi)}${suffix}`
}

/** "Free", "250 SEK", or null. */
export const formatPrice = (price, currency, isFree) => {
  if (isFree) return 'Free'
  const value = Number(price)
  if (!Number.isFinite(value) || value <= 0) return null
  return `${compactNumber(value)}${currency ? ` ${String(currency).toUpperCase()}` : ''}`
}

/** Trim to a word boundary and add a real ellipsis. */
export const truncate = (text, max = 120) => {
  if (typeof text !== 'string') return ''
  const clean = text.replace(/\s+/g, ' ').trim()
  if (clean.length <= max) return clean
  const cut = clean.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

/**
 * Only ever produce an href we are willing to navigate to. Scraped URLs are
 * untrusted input: without this check a stored "javascript:..." value would
 * become a working XSS vector the moment it is put in an anchor.
 */
export const safeUrl = (url) => {
  if (typeof url !== 'string' || !url) return null
  try {
    // Parsed with NO base. Supplying window.location.origin as a base would
    // resolve a scheme-less scraped value like "careers.acme.com/job/1" — or a
    // bare "//evil.com" — into a link that looks like it belongs to this site.
    // These URLs must be absolute and http(s) to be linkable at all.
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null
  } catch {
    return null
  }
}

/** Join non-empty parts with a separator, returning null when nothing remains. */
export const joinParts = (parts, separator = ' · ') => {
  const kept = (parts || []).filter((p) => p !== null && p !== undefined && p !== '')
  return kept.length ? kept.join(separator) : null
}

/**
 * A stable pastel pair per organisation, used for the monogram tile that stands
 * in for a logo (we store no logo images). Hashing the key means a company keeps
 * the same colour across sessions and across the card and the modal.
 */
const MONOGRAM_PALETTE = [
  ['#F8EBC5', '#E8C46B'],
  ['#FAD1CB', '#E79A8E'],
  ['#EDC4E4', '#C97FB8'],
  ['#DDCAF1', '#9E7FC9'],
  ['#C8DFF0', '#6C9FC4'],
  ['#C9EBD8', '#5FA983'],
]

/**
 * Parse "#RRGGBB" into {r,g,b}, or null. Company brand colours are authored, so
 * anything malformed has to fall through rather than throw.
 */
const parseHex = (hex) => {
  if (typeof hex !== 'string') return null
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

/**
 * Text colour that stays legible on an arbitrary brand colour.
 *
 * Uses relative luminance rather than a naive brightness average, so a saturated
 * mid-green and a mid-blue of the same "brightness" get the correct answer.
 */
export const readableOn = (hex, { light = '#FFFFFF', dark = '#221733' } = {}) => {
  const rgb = parseHex(hex)
  if (!rgb) return light

  const channel = (c) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  const luminance = 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
  return luminance > 0.45 ? dark : light
}

/** A brand colour at a given alpha, for washes and scrims. */
export const brandAlpha = (hex, alpha) => {
  const rgb = parseHex(hex)
  if (!rgb) return null
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

/** Darken a brand colour towards black, for a gradient's far end. */
export const shade = (hex, amount = 0.35) => {
  const rgb = parseHex(hex)
  if (!rgb) return null
  const f = Math.max(0, Math.min(1, 1 - amount))
  return `rgb(${Math.round(rgb.r * f)}, ${Math.round(rgb.g * f)}, ${Math.round(rgb.b * f)})`
}

export const monogramColors = (key) => {
  const text = String(key || '')
  let hash = 0
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0
  }
  const [background, foreground] = MONOGRAM_PALETTE[hash % MONOGRAM_PALETTE.length]

  // `foreground` is a deeper tint of `background` — good for a ring or a border,
  // but only ~1.4:1 against it, so initials painted in it are close to invisible.
  // `ink` is the colour to use for the letters themselves: one fixed, legible
  // brand purple that reads against every pastel in the palette.
  return { background, foreground, ink: '#432C5B' }
}
